import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

export interface ListParams {
  q: string;
  status: string;
  from: string;
  to: string;
  page: number;
  pageSize: number;
}

/** Reads list filters from the URL (so they survive reloads and links). */
export function readListParams(sp: URLSearchParams): ListParams {
  const num = (k: string, d: number) => {
    const n = Number(sp.get(k));
    return Number.isInteger(n) && n > 0 ? n : d;
  };
  return {
    q: sp.get('q') ?? '',
    status: sp.get('status') ?? '',
    from: sp.get('from') ?? '',
    to: sp.get('to') ?? '',
    page: num('page', 1),
    pageSize: Math.min(100, num('pageSize', 25)),
  };
}

/** List filters kept in the URL; changing any filter goes back to page 1. */
export function useListParams() {
  const [sp, setSp] = useSearchParams();
  const params = readListParams(sp);
  const set = useCallback(
    (patch: Partial<ListParams>) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          const resetPage = !('page' in patch);
          for (const [k, v] of Object.entries(patch)) {
            if (v === '' || (k === 'page' && v === 1) || (k === 'pageSize' && v === 25))
              next.delete(k);
            else next.set(k, String(v));
          }
          if (resetPage) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSp],
  );
  return [params, set] as const;
}

/** Only the filters the API understands, without empty values. */
export function apiFilters(p: ListParams) {
  return {
    page: p.page,
    pageSize: p.pageSize,
    ...(p.q ? { q: p.q } : {}),
    ...(p.from ? { from: p.from } : {}),
    ...(p.to ? { to: p.to } : {}),
  };
}
